import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Button, Card, Skeleton, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { useSqlSection } from './useSqlSection';
import { TabEmptyState } from '../../components/TabEmptyState/TabEmptyState';

/**
 * Read-only SQL view for the gen-3 cohort detail SQL tab (M4 workspace
 * design promotes SQL from a Definition-tab section to its own tab).
 *
 * Card spec per the Figma SQL frame: no heading (the SQL tab label
 * already names the surface) — the highlighted SQL starts at the top of
 * the card with a Copy SQL text button beside it, rendered directly on
 * the white card (no tinted box), and a tall centered empty state
 * ("No SQL") while the Research Assistant hasn't drafted a definition.
 *
 * SQL is generated on-the-fly via the platform's translate endpoint
 * (see `useCohortTranslationQuery`), formatted with `sql-formatter`,
 * and rendered through `react-syntax-highlighter` for parity with
 * the legacy SQL view.
 *
 * Width-fluid: the highlighter scrolls horizontally for long lines
 * and the outer card never forces overflow on narrow screens.
 */
export const SqlSection = () => {
  const theme = useTheme();
  const intl = useIntl();
  const { sqlValue, isLoading, hasUnresolvedRefs } = useSqlSection();

  const [copied, setCopied] = useState(false);

  // Revert the button label a beat after a successful copy.
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sqlValue);
      setCopied(true);
    } catch {
      // Clipboard unavailable (permissions / insecure context) — leave the
      // label unchanged rather than claiming a copy that didn't happen.
    }
  };

  return (
    <Card
      data-testid="cohort-detail-sql"
      variant="outlined"
      sx={{
        // Figma SQL card: 24px padding, 16px heading-to-code gap.
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
      }}
    >
      {isLoading && !sqlValue ? (
        <Skeleton variant="rectangular" height={120} data-testid="cohort-detail-sql-skeleton" />
      ) : hasUnresolvedRefs ? (
        // Backend short-circuited because the cohort references a library AV
        // that hasn't been bound yet. Decoupled from save state (OR-6067) —
        // the cohort is saved, we just can't compile SQL for it yet.
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="cohort-detail-sql-unresolved-refs"
        >
          One or more criteria reference variables that haven't been resolved yet. SQL preview and
          cohort execution will be available once every criterion is bound.
        </Typography>
      ) : !sqlValue ? (
        // No SQL until the cohort has an index date to compile against.
        <TabEmptyState
          data-testid="cohort-detail-sql-empty"
          title="No SQL"
          message="Add an index date to view the cohort SQL."
        />
      ) : (
        // Single row: the SQL starts at the top of the card with Copy SQL
        // beside it, instead of a header row pushing the code down.
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, minWidth: 0 }}>
          <Box
            data-testid="cohort-detail-sql-content"
            sx={{
              // SQL renders directly on the white card per the Figma SQL tab
              // frame — no tinted background box.
              flex: 1,
              overflowX: 'auto',
              minWidth: 0,
              // Prism injects its own padding + background via the language
              // stylesheet; neutralize both so the card surface shows through.
              '& pre': {
                margin: 0,
                padding: '0 !important',
                fontSize: `${theme.typography.caption.fontSize} !important`,
                lineHeight: '1.6 !important',
                backgroundColor: 'transparent !important',
              },
            }}
          >
            <SyntaxHighlighter language="sql" style={oneLight}>
              {sqlValue}
            </SyntaxHighlighter>
          </Box>
          <Button
            data-testid="cohort-detail-sql-copy"
            size="small"
            startIcon={<ContentCopyIcon sx={{ fontSize: 18 }} />}
            onClick={handleCopy}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            {copied
              ? intl.formatMessage({
                  id: 'projects.cohortBuilderPage.cohortBuilderSqlContent.copied',
                })
              : intl.formatMessage({
                  id: 'projects.cohortBuilderPage.cohortBuilderSqlContent.copy',
                })}
          </Button>
        </Box>
      )}
    </Card>
  );
};
